import React, { useState, useEffect } from "react";
import { Select, Switch, Checkbox, Space, Radio, message, Button } from "antd";
import { getRequest, postRequest } from "../service";

const tabList = [
  { key: "general", label: "General" },
  { key: "applicable", label: "Applicable" },
  { key: "restrictions", label: "Restrictions" },
];

const employeeTypeOptions = [
  { value: "intership", label: "Intership" },
  { value: "regualer", label: "Regual" },
  { value: "contract", label: "contract" },
];

const religionOptions = [
  { value: "Islam", label: "Islam" },
  { value: "Christianity", label: "Christianity" },
  { value: "Hinduism", label: "Hinduism" },
  { value: "Buddhism", label: "Buddhism" },
  { value: "Judaism", label: "Judaism" },
  { value: "Sikhism", label: "Sikhism" },
];

const AddLeaves = ({ onClose }) => {
  const [activeTab, setActiveTab] = useState("general");
  const [locationOptions, setLocationOptions] = useState([]);
  const [departmentOptions, setDepartmentOptions] = useState([]);
  const [designationOptions, setDesignationOptions] = useState([]);
  const [employeeOptions, setEmployeeOptions] = useState([]);
  const [loading, setLoading] = useState({
    locations: false,
    departments: false,
    designations: false,
    employees: false
  });

  const [formData, setFormData] = useState({
    title: "",
    type: "paid",
    duration: "yearly",
    days: "",
    requireReason: false,
    description: "",
    gender: "male,female,other",
    maritalStatus: "single,married,other",
    leaveAppliedOnly: "permanent",
    locations: [],
    departments: [],
    designations: [],
    employeeTypes: [],
    employees: [],
    religions: [],
    leavesPerMonth: "2",
    allowPastDates: true,
    pastDays: "7",
    allowFutureDates: true,
    futureDays: "7",
    weekendHandling: "dont_count",
    weekendCountAfter: ""
  });

  useEffect(() => {
    fetchLocations();
    fetchDepartments();
    fetchDesignations();
    fetchEmployees();
  }, []);

  // New useEffect to handle default selections
  useEffect(() => {
    // Set default selections when options are loaded
    if (locationOptions.length > 0) {
      setFormData(prev => ({
        ...prev,
        locations: locationOptions.map(opt => opt.value)
      }));
    }
  }, [locationOptions]);

  useEffect(() => {
    if (departmentOptions.length > 0) {
      setFormData(prev => ({
        ...prev,
        departments: departmentOptions.map(opt => opt.value)
      }));
    }
  }, [departmentOptions]);

  useEffect(() => {
    if (designationOptions.length > 0) {
      setFormData(prev => ({
        ...prev,
        designations: designationOptions.map(opt => opt.value)
      }));
    }
  }, [designationOptions]);

  useEffect(() => {
    if (employeeOptions.length > 0) {
      setFormData(prev => ({
        ...prev,
        employees: employeeOptions.map(opt => opt.value)
      }));
    }
  }, [employeeOptions]);

  // Set default selections for static options
  useEffect(() => {
    setFormData(prev => ({
      ...prev,
      employeeTypes: employeeTypeOptions.map(opt => opt.value),
      religions: religionOptions.map(opt => opt.value)
    }));
  }, []);

  const fetchLocations = async () => {
    try {
      setLoading(prev => ({ ...prev, locations: true }));
      const response = await getRequest('/api/companies/locations');
      if (response?.locations) {
        const formattedOptions = response.locations.map(item => ({
          value: item._id,
          label: item.locationName
        }));
        setLocationOptions(formattedOptions);
      }
    } catch (error) {
      console.error('Error fetching locations:', error);
    } finally {
      setLoading(prev => ({ ...prev, locations: false }));
    }
  };

  const fetchDepartments = async () => {
    try {
      setLoading(prev => ({ ...prev, departments: true }));
      const response = await getRequest('/api/department');
      if (response?.departments) {
        const formattedOptions = response.departments.map(item => ({
          value: item._id,
          label: item.name,
          children: item.subDepartments?.map(subDept => ({
            value: subDept._id,
            label: subDept.name
          }))
        }));
        setDepartmentOptions(formattedOptions);
      }
    } catch (error) {
      console.error('Error fetching departments:', error);
    } finally {
      setLoading(prev => ({ ...prev, departments: false }));
    }
  };

  const fetchDesignations = async () => {
    try {
      setLoading(prev => ({ ...prev, designations: true }));
      const response = await getRequest('/api/companies/designation');
      if (response?.designations) {
        const formattedOptions = response.designations.map(item => ({
          value: item._id,
          label: item.designationName
        }));
        setDesignationOptions(formattedOptions);
      }
    } catch (error) {
      console.error('Error fetching designations:', error);
    } finally {
      setLoading(prev => ({ ...prev, designations: false }));
    }
  };

  const fetchEmployees = async () => {
    try {
      setLoading(prev => ({ ...prev, employees: true }));
      const response = await getRequest('/api/companies/reportto');
      if (response?.employees) {
        const formattedOptions = response.employees.map(item => ({
          value: item._id,
          label: item.name
        }));
        setEmployeeOptions(formattedOptions);
      }
    } catch (error) {
      console.error('Error fetching employees:', error);
    } finally {
      setLoading(prev => ({ ...prev, employees: false }));
    }
  };

  const handleChange = (name, value) => {
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCheckboxChange = (type, value) => {
    setFormData(prev => {
      const currentValues = prev[type].split(',');
      const newValues = currentValues.includes(value)
        ? currentValues.filter(v => v !== value)
        : [...currentValues, value];
      return { ...prev, [type]: newValues.join(',') };
    });
  };

  const handleSelectAll = (type, options) => {
    setFormData(prev => ({
      ...prev,
      [type]: options.map(opt => opt.value).join(',')
    }));
  };

  const handleUnselectAll = (type) => {
    setFormData(prev => ({
      ...prev,
      [type]: ""
    }));
  };

  const getSelectOptions = (options, type) => {
    return [
      {
        label: (
          <div className="flex justify-between items-center">
            <span>Select All</span>
            <span className="text-xs text-gray-500">Select all options</span>
          </div>
        ),
        value: 'select-all',
        className: 'select-all-option'
      },
      {
        label: (
          <div className="flex justify-between items-center">
            <span>Unselect All</span>
            <span className="text-xs text-gray-500">Clear all selections</span>
          </div>
        ),
        value: 'unselect-all',
        className: 'unselect-all-option'
      },
      {
        type: 'divider'
      },
      ...options
    ];
  };

  const handleSelectChange = (type, value, options) => {
    if (value.includes('select-all')) {
      handleSelectAll(type, options);
    } else if (value.includes('unselect-all')) {
      handleUnselectAll(type);
    } else {
      handleChange(type, value.filter(v => v !== 'select-all' && v !== 'unselect-all'));
    }
  };

  const handleSubmit = async () => {
    try {
      const payload = {
        title: formData.title,
        type: formData.type,
        leaveDuration: formData.duration.charAt(0).toUpperCase() + formData.duration.slice(1),
        numberOfDays: parseInt(formData.days),
        isReasonMandatory: formData.requireReason,
        description: formData.description,
        gender: formData.gender,
        maritalStatus: formData.maritalStatus,
        leaveAppliedOnly: formData.leaveAppliedOnly,
        location: formData.locations,
        department: formData.departments,
        designation: formData.designations,
        employeeType: formData.employeeTypes,
        employees: formData.employees,
        religion: formData.religions,
        employeeGroups: [],
        leavesAllowedPerMonth: parseInt(formData.leavesPerMonth),
        allowPastDates: formData.allowPastDates ? parseInt(formData.pastDays) : 0,
        allowFutureDates: formData.allowFutureDates ? parseInt(formData.futureDays) : 0,
        includeWeekends: formData.weekendHandling === "count" ? "true" : "false"
      };

      const response = await postRequest('/api/leaves', payload);
      if (response.message === "Leaves created successfully") {
        message.success('Leaves created successfully');
        onClose();
      }
      else{
        message.error(response.error || "Leaves Not created")
      }
    } catch (error) {
      console.error('Error submitting leave form:', error);
    }
  };

  return (
    <>
      {/* Tabs */}
      <div className="flex relative w-full mb-4">
        {tabList.map((tab) => (
          <button
            key={tab.key}
            className={`px-4 text-md outline-none rounded-t-md py-2 cursor-pointer
              ${
                activeTab === tab.key
                  ? "text-active-color border border-gray-400 border-b-0 bg-white"
                  : "text-gray-500 border-b border-gray-400"
              }
            `}
            style={{
              borderBottomLeftRadius: activeTab === tab.key ? "6px" : "0",
              borderBottomRightRadius: activeTab === tab.key ? "6px" : "0",
              borderTopLeftRadius: "6px",
              borderTopRightRadius: "6px",
              marginRight: "2px",
              zIndex: activeTab === tab.key ? 2 : 1,
              position: "relative",
              top: activeTab === tab.key ? "1px" : "0"
            }}
            onClick={() => setActiveTab(tab.key)}
          >
            {tab.label}
          </button>
        ))}
        <div className="absolute left-0 right-0 bottom-0 border-b border-gray-400" style={{zIndex: 0}} />
      </div>

      {/* Tab Content */}
      <div className="flex flex-col gap-4 h-[50vh] pr-3 overflow-y-auto">
        {activeTab === "general" && (
          <>
            {/* Title */}
            <div className="flex flex-col gap-1">
              <label className="text-md">
                Title <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => handleChange("title", e.target.value)}
                className="border border-gray-400 outline-none px-2 py-1 rounded-md text-md"
                placeholder="Enter leave title"
              />
            </div>

            {/* Type */}
            <div className="flex flex-col gap-1">
              <label className="text-md">
                Type <span className="text-red-500">*</span>
              </label>
              <Select
                value={formData.type}
                onChange={(value) => handleChange("type", value)}
                options={[
                  { value: "paid", label: "Paid" },
                  { value: "unpaid", label: "Unpaid" }
                ]}
              />
            </div>

            {/* Duration and Days in one line */}
            <div className="grid grid-cols-2 gap-4">
              <div className="flex flex-col gap-1">
                <label className="text-md">
                  Leave Duration <span className="text-red-500">*</span>
                </label>
                <Select
                  value={formData.duration}
                  onChange={(value) => handleChange("duration", value)}
                  options={[
                    { value: "monthly", label: "Monthly" },
                    { value: "yearly", label: "Yearly" }
                  ]}
                />
              </div>

              <div className="flex flex-col gap-1">
                <label className="text-md">
                  Number of Days <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  value={formData.days}
                  onChange={(e) => handleChange("days", e.target.value)}
                  className="border border-gray-400 outline-none px-2 py-1 rounded-md text-md"
                  placeholder="Enter number of days"
                />
              </div>
            </div>

            {/* Reason Required Toggle */}
            <div className="flex items-center justify-between">
              <label className="text-md">Is providing a reason for leave mandatory?</label>
              <Switch
                checked={formData.requireReason}
                onChange={(checked) => handleChange("requireReason", checked)}
              />
            </div>

            {/* Description */}
            <div className="flex flex-col gap-1 mb-2">
              <label className="text-md">Description</label>
              <textarea
                value={formData.description}
                onChange={(e) => handleChange("description", e.target.value)}
                className="border border-gray-400 outline-none px-2 py-1 rounded-md text-md"
                placeholder="Enter description"
                rows={3}
              />
            </div>
          </>
        )}

        {activeTab === "applicable" && (
          <div className="flex flex-col gap-4">
            {/* Gender Section */}
            <div className="flex flex-col gap-2">
              <label className="text-md font-medium">Gender</label>
              <Space className="flex flex-row gap-2" direction="horizontal">
                <Checkbox
                  checked={formData.gender.split(',').includes("male")}
                  onChange={() => handleCheckboxChange("gender", "male")}
                >
                  Male
                </Checkbox>
                <Checkbox
                  checked={formData.gender.split(',').includes("female")}
                  onChange={() => handleCheckboxChange("gender", "female")}
                >
                  Female
                </Checkbox>
                <Checkbox
                  checked={formData.gender.split(',').includes("other")}
                  onChange={() => handleCheckboxChange("gender", "other")}
                >
                  Other
                </Checkbox>
              </Space>
            </div>

            {/* Marital Status Section */}
            <div className="flex flex-col gap-2">
              <label className="text-md font-medium">Marital Status</label>
              <Space className="flex flex-row gap-2" direction="horizontal">
                <Checkbox
                  checked={formData.maritalStatus.split(',').includes("single")}
                  onChange={() => handleCheckboxChange("maritalStatus", "single")}
                >
                  Single
                </Checkbox>
                <Checkbox
                  checked={formData.maritalStatus.split(',').includes("married")}
                  onChange={() => handleCheckboxChange("maritalStatus", "married")}
                >
                  Married
                </Checkbox>
                <Checkbox
                  checked={formData.maritalStatus.split(',').includes("other")}
                  onChange={() => handleCheckboxChange("maritalStatus", "other")}
                >
                  Other
                </Checkbox>
              </Space>
            </div>

            {/* Leave Applied Only Section */}
            <div className="flex flex-col gap-2">
              <label className="text-md font-medium">Leave Applied only</label>
              <Radio.Group
                value={formData.leaveAppliedOnly}
                onChange={(e) => handleChange("leaveAppliedOnly", e.target.value)}
              >
                <Space className="flex flex-row gap-2" direction="horizontal">
                  <Radio value="probation">Probation</Radio>
                  <Radio value="permanent">Permanent</Radio>
                </Space>
              </Radio.Group>
            </div>

            {/* Select Fields Section */}
            <div className="flex flex-col gap-4">
              {/* Location */}
              <div className="flex flex-col gap-1">
                <label className="text-md">Location</label>
                <Select
                  mode="multiple"
                  value={formData.locations}
                  onChange={(value) => handleSelectChange("locations", value, locationOptions)}
                  options={getSelectOptions(locationOptions, "locations")}
                  className="w-full"
                  placeholder="Select locations"
                  loading={loading.locations}
                  maxTagCount={3}
                  maxTagTextLength={20}
                />
              </div>

              {/* Department */}
              <div className="flex flex-col gap-1">
                <label className="text-md">Department</label>
                <Select
                  mode="multiple"
                  value={formData.departments}
                  onChange={(value) => handleSelectChange("departments", value, departmentOptions)}
                  options={getSelectOptions(departmentOptions, "departments")}
                  className="w-full"
                  placeholder="Select departments"
                  loading={loading.departments}
                  treeData={departmentOptions}
                  treeCheckable={true}
                  showCheckedStrategy="SHOW_PARENT"
                  maxTagCount={3}
                  maxTagTextLength={20}
                />
              </div>

              {/* Designation */}
              <div className="flex flex-col gap-1">
                <label className="text-md">Designation</label>
                <Select
                  mode="multiple"
                  value={formData.designations}
                  onChange={(value) => handleSelectChange("designations", value, designationOptions)}
                  options={getSelectOptions(designationOptions, "designations")}
                  className="w-full"
                  placeholder="Select designations"
                  loading={loading.designations}
                  maxTagCount={3}
                  maxTagTextLength={20}
                />
              </div>

              {/* Employee Type */}
              <div className="flex flex-col gap-1">
                <label className="text-md">Employee Type</label>
                <Select
                  mode="multiple"
                  value={formData.employeeTypes}
                  onChange={(value) => handleSelectChange("employeeTypes", value, employeeTypeOptions)}
                  options={getSelectOptions(employeeTypeOptions, "employeeTypes")}
                  className="w-full"
                  placeholder="Select employee types"
                  maxTagCount={3}
                  maxTagTextLength={20}
                />
              </div>

              {/* Employees */}
              <div className="flex flex-col gap-1">
                <label className="text-md">Employees</label>
                <Select
                  mode="multiple"
                  value={formData.employees}
                  onChange={(value) => handleSelectChange("employees", value, employeeOptions)}
                  options={getSelectOptions(employeeOptions, "employees")}
                  className="w-full"
                  placeholder="Select employees"
                  loading={loading.employees}
                  maxTagCount={3}
                  maxTagTextLength={20}
                />
              </div>

              {/* Religion */}
              <div className="flex flex-col gap-1">
                <label className="text-md">Religion</label>
                <Select
                  mode="multiple"
                  value={formData.religions}
                  onChange={(value) => handleSelectChange("religions", value, religionOptions)}
                  options={getSelectOptions(religionOptions, "religions")}
                  className="w-full"
                  placeholder="Select religions"
                  maxTagCount={3}
                  maxTagTextLength={20}
                />
              </div>
            </div>
          </div>
        )}

        {activeTab === "restrictions" && (
          <div className="flex flex-col gap-4">
            {/* Number of Leaves Allowed Per Month */}
            <div className="flex flex-col gap-1">
              <label className="text-md">
                Number of Leaves Allowed Per Month <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                value={formData.leavesPerMonth}
                onChange={(e) => handleChange("leavesPerMonth", e.target.value)}
                className="border border-gray-400 outline-none px-2 py-1 rounded-md text-md"
                placeholder="Enter number of leaves allowed per month"
              />
            </div>

            {/* Past Dates Section */}
            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-2">
                <Checkbox
                  checked={formData.allowPastDates}
                  onChange={(e) => handleChange("allowPastDates", e.target.checked)}
                >
                  Allow Requests For Past Dates
                </Checkbox>
              </div>
              {formData.allowPastDates && (
                <div className="flex items-center gap-2 ml-6">
                  <span className="text-md">Past</span>
                  <input
                    type="number"
                    value={formData.pastDays}
                    onChange={(e) => handleChange("pastDays", e.target.value)}
                    className="border border-gray-400 outline-none px-2 py-1 rounded-md text-md w-20"
                    placeholder="Days"
                  />
                  <span className="text-md">Days</span>
                </div>
              )}
            </div>

            {/* Future Dates Section */}
            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-2">
                <Checkbox
                  checked={formData.allowFutureDates}
                  onChange={(e) => handleChange("allowFutureDates", e.target.checked)}
                >
                  Allow Requests For Future Dates
                </Checkbox>
              </div>
              {formData.allowFutureDates && (
                <div className="flex items-center gap-2 ml-6">
                  <span className="text-md">Next</span>
                  <input
                    type="number"
                    value={formData.futureDays}
                    onChange={(e) => handleChange("futureDays", e.target.value)}
                    className="border border-gray-400 outline-none px-2 py-1 rounded-md text-md w-20"
                    placeholder="Days"
                  />
                  <span className="text-md">Days</span>
                </div>
              )}
            </div>

            {/* Weekend Handling Section */}
            <div className="flex flex-col gap-2">
              <label className="text-md font-medium">Weekends Between Leave Period</label>
              <div className="flex flex-col gap-2 ml-2">
                <Radio.Group 
                  value={formData.weekendHandling}
                  onChange={(e) => handleChange("weekendHandling", e.target.value)}
                >
                  <Space direction="vertical">
                    <Radio value="count">Count as leave</Radio>
                    
                    <Radio value="dont_count">Don't count as leave</Radio>
                  </Space>
                </Radio.Group>
              </div>
            </div>
          </div>
        )}
      </div>
      {/* Buttons */}
      <div className="flex items-center justify-end gap-3 mt-2">
        <Button onClick={onClose} >Cancel</Button>
        <Button
          onClick={handleSubmit}
          type="primary"
          className="bg-primary text-white"
        >
          Save
        </Button>
      </div>
    </>
  );
};

export default AddLeaves;
